import React, { FC, useState, useEffect, useCallback } from 'react';
import { Button, Divider, Grid, makeStyles, Paper, Theme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import axios, { CancelTokenSource } from 'axios';

import SearchInput from 'components/SearchInput';
import useDebounce from 'hooks/useDebounce';
import JobActionTagTable from './components/JobActionTagTable';
import ActionSnackbar from 'components/ActionSnackbar';

import { JOB_ACTION_TAG_TEMPLATE_BASE_URL, GET_REORDER_JOB_ACTION_TAG_TEMPLATE_URL } from 'constants/url';
import LoadingButtonIndicator from 'components/LoadingButtonIndicator';

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    margin: 'auto'
  },
  addButton: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1)
  },
  extendedIcon: {
    paddingRight: theme.spacing(1)
  }
}));

const JobActionTagTemplate: FC = () => {
  const classes = useStyles();

  const [query, setQuery] = useState<string>('');
  const [queryString, setQueryString] = useState<string>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [refetchTrigger, setRefetchTrigger] = useState<number>(0);

  const [isSearchingJobActionTagTemplate, setSearchingJobActionTagTemplate] = useState<boolean>(false);
  const [, setSearchJobActionTagTemplateError] = useState<boolean>(false);
  const [jobActionTagTemplates, setJobActionTagTemplates] = useState<JobActionTagTemplateModel[]>([]);
  const [count, setCount] = useState<number>(0);

  const [openCreateJobActionTagTemplate, setOpenCreateJobActionTagTemplate] = useState<boolean>(false);
  const [openEditJobActionTagTemplate, setOpenEditJobActionTagTemplate] = useState<boolean>(false);
  const [currentEditingJobActionTagTemplateIndex, setCurrentEditingJobActionTagTemplateIndex] = useState<number>(0);

  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarVarient, setSnackbarVarient] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  const handleSnackbar = (variant: 'success' | 'error', snackbarMessage: string) => {
    setOpenSnackbar(true);
    setSnackbarVarient(variant);
    setSnackbarMessage(snackbarMessage);
  };

  // Search JobActionTagTemplate whenever rowsPerPage, currentPage, queryString changes
  useEffect(() => {
    const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

    const getQueryParams = () => {
      const params = new URLSearchParams();
      if (queryString) {
        params.append('q', queryString);
      }

      params.append('s', (currentPage * rowsPerPage).toString());
      params.append('l', rowsPerPage.toString());

      return params.toString();
    };

    const searchJobActionTagTemplate = async () => {
      setSearchingJobActionTagTemplate(true);
      setSearchJobActionTagTemplateError(false);

      try {
        const url = `${JOB_ACTION_TAG_TEMPLATE_BASE_URL}?${getQueryParams()}`;
        const { data } = await axios.get(url, { cancelToken: cancelTokenSource.token });
        setCount(data.count);
        setJobActionTagTemplates(data.jobActionTagTemplates);
      } catch (err) {
        setSearchJobActionTagTemplateError(true);
      }

      setSearchingJobActionTagTemplate(false);
    };

    searchJobActionTagTemplate();

    return () => {
      cancelTokenSource.cancel();
    };
  }, [rowsPerPage, currentPage, queryString, refetchTrigger]);

  const performActionAndRevertPage = (action: React.Dispatch<React.SetStateAction<any>>, actionParam: any) => {
    setCurrentPage(0);
    action(actionParam);
  };

  const handleSearch = useCallback((searchQuery: string) => {
    performActionAndRevertPage(setQueryString, searchQuery);
  }, []);

  const debouncedSearchTerm = useDebounce(query, 500);
  // Load client data to populate on search list
  useEffect(() => {
    if (debouncedSearchTerm.length >= 2) {
      handleSearch(debouncedSearchTerm);
    } else if (debouncedSearchTerm.length === 0) {
      handleSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, handleSearch]);

  const handleOpenCreateJobActionTagTemplate = () => {
    setOpenEditJobActionTagTemplate(false);
    setOpenCreateJobActionTagTemplate(true);
  };

  const handleCancelCreateJobActionTagTemplate = () => {
    setOpenCreateJobActionTagTemplate(false);
  };

  const handleOpenEditJobActionTagTemplate = (skillIndex: number): React.MouseEventHandler => () => {
    setCurrentEditingJobActionTagTemplateIndex(skillIndex);
    setOpenCreateJobActionTagTemplate(false);
    setOpenEditJobActionTagTemplate(true);
  };

  const handleCancelEditJobActionTagTemplate = () => {
    setOpenEditJobActionTagTemplate(false);
  };

  const addNewJobActionTagTemplate = (jobActionTagTemplate: JobActionTagTemplateModel) => {
    setOpenCreateJobActionTagTemplate(false);
    setCount(c => c + 1);
    setRefetchTrigger(prev => prev + 1);
  };

  const updateIndividualJobActionTagTemplate = (updatedJobActionTagTemplateProperties: Partial<JobActionTagTemplateModel>) => {
    setJobActionTagTemplates(
      jobActionTagTemplates!.map((jobActionTagTemplate, index) => {
        if (index !== currentEditingJobActionTagTemplateIndex) {
          return jobActionTagTemplate;
        }

        return Object.assign({}, jobActionTagTemplate, updatedJobActionTagTemplateProperties);
      })
    );
  };

  const deleteIndividualJobActionTagTemplate = (skillIndex: number) => {
    jobActionTagTemplates.splice(skillIndex, 1);
    setJobActionTagTemplates([...jobActionTagTemplates]);
    setCount(c => c - 1);
  };

  const onReorderJobActionTagTemplate = async (jobActionTagTemplateId: number, direction: 'up' | 'down') => {
    try {
      await axios.put(GET_REORDER_JOB_ACTION_TAG_TEMPLATE_URL(jobActionTagTemplateId), { direction });
      handleSnackbar('success', `Successfully moved ${direction === 'up' ? 'up' : 'down'}`);
      // Refresh the data after reordering by incrementing the refetch trigger
      setRefetchTrigger(prev => prev + 1);
    } catch (err) {
      handleSnackbar('error', `Failed to reorder job action tag template`);
    }
  };

  return (
    <Paper variant='outlined' className={classes.paper}>
      <Grid container justify='space-between' style={{ padding: 16 }}>
        <SearchInput
          withBorder
          withTransition={false}
          width={150}
          placeHolder='Search Job Action Tag...'
          iconColor='#989898'
          tableSearchValue={query}
          setTableSearchValue={setQuery}
        />
        <Button
          id='add_button'
          color='primary'
          size='medium'
          variant='contained'
          disabled={isSearchingJobActionTagTemplate}
          disableElevation
          className={classes.addButton}
          startIcon={<AddIcon />}
          onClick={() => {
            handleOpenCreateJobActionTagTemplate();
          }}
        >
          New Template
          <LoadingButtonIndicator isLoading={isSearchingJobActionTagTemplate} />
        </Button>
      </Grid>
      <Divider style={{ marginTop: 16 }} />
      <JobActionTagTable
        isLoadingData={isSearchingJobActionTagTemplate}
        jobActionTagTemplates={jobActionTagTemplates}
        count={count}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        handleChangePage={(event, page) => setCurrentPage(page)}
        handleChangeRowsPerPage={event => performActionAndRevertPage(setRowsPerPage, +event.target.value)}
        openCreateJobActionTagTemplate={openCreateJobActionTagTemplate}
        handleCancelCreateJobActionTagTemplate={handleCancelCreateJobActionTagTemplate}
        addNewJobActionTagTemplate={addNewJobActionTagTemplate}
        openEditJobActionTagTemplate={openEditJobActionTagTemplate}
        jobActionTagTemplate={jobActionTagTemplates[currentEditingJobActionTagTemplateIndex]}
        currentEditingJobActionTagTemplateIndex={currentEditingJobActionTagTemplateIndex}
        handleOpenEditJobActionTagTemplate={handleOpenEditJobActionTagTemplate}
        handleCancelEditJobActionTagTemplate={handleCancelEditJobActionTagTemplate}
        updateIndividualJobActionTagTemplate={updateIndividualJobActionTagTemplate}
        deleteIndividualJobActionTagTemplate={deleteIndividualJobActionTagTemplate}
        onReorderJobActionTagTemplate={onReorderJobActionTagTemplate}
        handleSnackbar={handleSnackbar}
      />
      <ActionSnackbar
        variant={snackbarVarient}
        message={snackbarMessage}
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
        Icon={snackbarVarient === 'success' ? CheckCircleIcon : ErrorIcon}
      />
    </Paper>
  );
};

export default JobActionTagTemplate;
